/**
 * /settings/posting-accounts — Settings → Accounting → Posting accounts
 * (wave 2 books-posting layer).
 *
 * HR money events (payroll run locked, F&F settlement, loan movements,
 * expense batches) post to the books by ROLE — salary expense, PF payable,
 * salary payable, employee loans, … Each role posts to a default account
 * of the chart; this page lets the accountant point a role at a different
 * live account of the same chart (e.g. a per-state PT payable, or a
 * gratuity PROVISION instead of the gratuity expense head).
 *
 * Two roles are fixed (trade payables follow the AP sub-ledger; expense
 * heads follow each category's GL code) and are shown read-only.
 * Re-mapping affects FUTURE postings only — posted journals never move.
 *
 * Account types (audit F22): each role maps only to accounts of the types
 * it allows (`allowed_account_types` — liability for the payables, expense
 * for salaries, asset for loans / bank, …), so the picker lists only those;
 * the PUT refuses anything else with 422 account_type_not_allowed. A
 * mapping saved before the rule keeps posting but is flagged
 * (`type_mismatch`) so it can be re-mapped.
 *
 * Who can change it (audit F32): the PUT is company_admin / hr_manager
 * only — exactly the canonical isHRAdmin set. Accountants read the page.
 *
 * Accounting-module page (the API is behind RequireFeature("accounting")):
 * without the module it shows an explanatory empty state.
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Landmark, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { toApiError } from "@/lib/api";
import { useAuth, useFeatures } from "@/lib/auth/auth-context";
import { isHRAdmin } from "@/lib/nav";
import { postingRoleHint } from "@/lib/posting-roles";
import { useAccounts } from "@/hooks/use-accounting";
import {
  useLedgerRoles,
  useUpsertLedgerRole,
} from "@/hooks/use-books-posting";
import type { Account, AccountType } from "@/types/accounting";
import type { LedgerRole } from "@/types/books-posting";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { useBelowSm } from "@/lib/use-below-sm";
import { StatusBadge } from "@/components/shared/status-badge";
import { EmptyState } from "@/components/shared/empty-state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/posting-accounts",
)({
  component: SettingsPostingAccountsRoute,
});

/** Select sentinel: revert to the role's default account code. */
const DEFAULT_ACCOUNT = "default";

const ACCOUNT_TYPE_WORDS: Record<AccountType, string> = {
  asset: "asset",
  liability: "liability",
  equity: "equity",
  income: "income",
  expense: "expense",
};

/** "Liability accounts only" / "Expense or liability accounts only". */
function allowedTypesHint(types: readonly AccountType[]): string | null {
  if (types.length === 0) return null;
  const words = types.map((t) => ACCOUNT_TYPE_WORDS[t] ?? t);
  const joined =
    words.length === 1
      ? words[0]
      : `${words.slice(0, -1).join(", ")} or ${words[words.length - 1]}`;
  return `${joined.charAt(0).toUpperCase()}${joined.slice(1)} accounts only`;
}

function RoleRow({
  role,
  accounts,
  readOnly,
  asCard = false,
}: Readonly<{
  role: LedgerRole;
  accounts: Account[];
  readOnly: boolean;
  asCard?: boolean;
}>) {
  const current = role.is_mapped && role.account_id != null ? String(role.account_id) : DEFAULT_ACCOUNT;
  const [value, setValue] = useState(current);
  const mut = useUpsertLedgerRole();
  const dirty = value !== current;
  // Only accounts of a type this role may post to (the PUT refuses any
  // other with 422 account_type_not_allowed). An older mapping to a
  // wrong-type account stays visible — disabled — so the select still
  // shows what is saved until it is re-mapped.
  const allowed = role.allowed_account_types ?? [];
  const choices = accounts.filter((a) => allowed.includes(a.type));
  const savedWrongType =
    role.is_mapped &&
    role.account_id != null &&
    !choices.some((a) => a.id === role.account_id)
      ? accounts.find((a) => a.id === role.account_id)
      : undefined;
  const typesHint = allowedTypesHint(allowed);
  // The shown value can be cut with an ellipsis, so the trigger carries it as a tooltip.
  const pick = choices.find((a) => String(a.id) === value) ?? (savedWrongType && String(savedWrongType.id) === value ? savedWrongType : undefined);
  const selectedLabel =
    value === DEFAULT_ACCOUNT
      ? `Default (${role.default_code})`
      : pick
        ? `${pick.code} · ${pick.name}${pick === savedWrongType ? " (Wrong Type)" : ""}`
        : undefined;
  // What posts here, in words (never the raw role code — re-test books #6).
  const hint = postingRoleHint(role.role);

  async function onSave() {
    try {
      const updated = await mut.mutateAsync({
        role: role.role,
        account_id: value === DEFAULT_ACCOUNT ? null : Number(value),
      });
      setValue(updated.is_mapped && updated.account_id != null ? String(updated.account_id) : DEFAULT_ACCOUNT);
      toast.success(
        updated.is_mapped
          ? `${role.label} will post to ${updated.account_code} ${updated.account_name ?? ""}.`
          : `${role.label} reverted to its default account ${role.default_code}.`,
      );
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  const effective =
    role.account_code != null
      ? `${role.account_code}${role.account_name ? ` · ${role.account_name}` : ""}`
      : role.default_code;

  const postingBlock = (
    <div className="min-w-0">
      <p className="text-sm font-medium">{role.label}</p>
      {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
  const accountBlock = (
    <>
      {role.mappable && !readOnly ? (
        <Select value={value} onValueChange={setValue} disabled={mut.isPending}>
          <SelectTrigger className="max-w-sm" aria-label={`Account for ${role.label}`} title={selectedLabel}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={DEFAULT_ACCOUNT}>
              Default ({role.default_code})
            </SelectItem>
            {savedWrongType && (
              <SelectItem value={String(savedWrongType.id)} disabled>
                {savedWrongType.code} · {savedWrongType.name} (Wrong Type)
              </SelectItem>
            )}
            {choices.map((a) => (
              <SelectItem key={a.id} value={String(a.id)}>
                {a.code} · {a.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <p className="text-sm">{effective}</p>
      )}
      {role.mappable && !readOnly && typesHint && (
        <p className="mt-1 text-xs text-muted-foreground">{typesHint}.</p>
      )}
      {!role.mappable && (
        <p className="mt-1 text-xs text-muted-foreground">
          Fixed — follows its source document.
        </p>
      )}
    </>
  );
  const statusBlock = (
    <div className="flex flex-col items-start gap-1">
      {role.needs_confirmation ? (
        <StatusBadge tone="warning" label="Confirm Account" />
      ) : role.target_missing ? (
        <StatusBadge tone="danger" label="Account Missing" />
      ) : (
        <StatusBadge
          tone={role.is_mapped ? "success" : "neutral"}
          label={role.is_mapped ? "Mapped" : "Default"}
        />
      )}
      {role.type_mismatch && (
        <StatusBadge
          tone="warning"
          label="Wrong Account Type — Re-Map"
          className="whitespace-normal text-left"
        />
      )}
    </div>
  );
  const saveButton = role.mappable ? (
    <Button
      size="sm"
      variant="outline"
      className="gap-1.5"
      onClick={() => void onSave()}
      disabled={!dirty || mut.isPending}
    >
      {mut.isPending ? (
        <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Save aria-hidden className="h-3.5 w-3.5" />
      )}
      Save
    </Button>
  ) : null;

  // Phones: a stacked card — posting + status, full-width account, Save.
  if (asCard) {
    return (
      <li className="space-y-2 rounded-md border p-3">
        <div className="flex items-start justify-between gap-2">
          {postingBlock}
          <div className="shrink-0">{statusBlock}</div>
        </div>
        <div>{accountBlock}</div>
        {!readOnly && saveButton && (
          <div className="grid [&>*]:w-full">{saveButton}</div>
        )}
      </li>
    );
  }

  return (
    <TableRow>
      <TableCell className="align-top">{postingBlock}</TableCell>
      <TableCell className="align-top">{accountBlock}</TableCell>
      <TableCell className="align-top">{statusBlock}</TableCell>
      {!readOnly && (
        <TableCell className="align-top text-right">{saveButton}</TableCell>
      )}
    </TableRow>
  );
}

function SettingsPostingAccountsRoute() {
  const booksOn = useFeatures().has("accounting");
  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold">Posting Accounts</h2>
            <p className="text-sm text-muted-foreground">
              Payroll, full-and-final settlements, employee loans and expense
              reimbursements post to your books automatically. Each line
              posts to the account shown here. Change one to post to another
              account of your chart (for example a gratuity provision instead
              of the gratuity expense head). Changes apply to future postings
              only.
            </p>
          </div>
          {booksOn ? (
            <PostingAccountsBody />
          ) : (
            <EmptyState
              icon={Landmark}
              title="Accounting is off"
              description="Switch the Accounting module on (Settings → Modules) to post payroll, F&F, loans and reimbursements to your books."
            />
          )}
        </div>
      </SettingsShell>
    </>
  );
}

/** Mounted only with the Accounting module on (its APIs are gated on it). */
function PostingAccountsBody() {
  const phone = useBelowSm();
  // The PUT is company_admin / hr_manager only (hrRole server-side) —
  // the canonical isHRAdmin set; accountants read only.
  const { user } = useAuth();
  const readOnly = !isHRAdmin(user?.role);
  const rolesQ = useLedgerRoles();
  const accountsQ = useAccounts();
  const accounts = (accountsQ.data?.items ?? []).filter((a) => !a.is_archived);
  const items = rolesQ.data?.items ?? [];
  const missing = items.filter((r) => r.target_missing && !r.needs_confirmation);
  const unconfirmed = items.filter((r) => r.needs_confirmation);
  const wrongType = items.filter((r) => r.type_mismatch);

  return (
    <>
          {readOnly && (
            <Alert>
              <AlertTitle>Read-only</AlertTitle>
              <AlertDescription>
                Only HR managers and company admins can change posting
                accounts.
              </AlertDescription>
            </Alert>
          )}

          {missing.length > 0 && (
            <Alert variant="destructive">
              <AlertTitle>Some postings will fail</AlertTitle>
              <AlertDescription>
                {missing.map((m) => m.label).join(", ")}{" "}
                {missing.length === 1 ? "points" : "point"} at an account that
                is archived or missing from your chart. Restore it under Chart
                of accounts or choose another account here — until then a
                payroll lock or F&F compute that needs it is refused.
              </AlertDescription>
            </Alert>
          )}

          {unconfirmed.length > 0 && (
            <Alert variant="destructive">
              <AlertTitle>Confirm the account for {unconfirmed.length === 1 ? "one posting" : `${unconfirmed.length} postings`}</AlertTitle>
              <AlertDescription>
                {unconfirmed
                  .map((m) => `${m.label} (${m.default_code} · ${m.account_name ?? ""})`)
                  .join(", ")}
                : the default code belongs to an account you created
                yourself, so WorkRight will not post to it on its own. Choose
                the right account for each — the same one if it really is
                that account — and save. Until then a payroll lock or F&F
                compute that needs it is refused.
              </AlertDescription>
            </Alert>
          )}

          {wrongType.length > 0 && (
            <Alert>
              <AlertTitle>
                {wrongType.length === 1
                  ? "One posting points at the wrong kind of account"
                  : `${wrongType.length} postings point at the wrong kind of account`}
              </AlertTitle>
              <AlertDescription>
                {wrongType.map((m) => m.label).join(", ")}: the account was
                chosen before WorkRight checked account types. Postings still
                go there.{" "}
                {readOnly
                  ? "Ask an HR manager or company admin to pick an account of the right type."
                  : "Pick an account of the right type and save."}
              </AlertDescription>
            </Alert>
          )}

          {(rolesQ.isLoading || accountsQ.isLoading) && (
            <div className="space-y-3">
              {["s1", "s2", "s3"].map((k) => (
                <Skeleton key={k} className="h-14 w-full" />
              ))}
            </div>
          )}

          {rolesQ.isError && (
            <Alert variant="destructive">
              <AlertTitle>Couldn&apos;t load posting accounts</AlertTitle>
              <AlertDescription>{toApiError(rolesQ.error).message}</AlertDescription>
            </Alert>
          )}

          {rolesQ.data && !accountsQ.isLoading && phone && (
            <ul className="space-y-3">
              {items.map((r) => (
                <RoleRow
                  key={`${r.role}:${r.is_mapped ? r.account_id : "d"}`}
                  role={r}
                  accounts={accounts}
                  readOnly={readOnly}
                  asCard
                />
              ))}
            </ul>
          )}

          {rolesQ.data && !accountsQ.isLoading && !phone && (
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-72">Posting</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead className="w-44">Status</TableHead>
                    {!readOnly && <TableHead className="w-24" />}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((r) => (
                    <RoleRow
                      key={`${r.role}:${r.is_mapped ? r.account_id : "d"}`}
                      role={r}
                      accounts={accounts}
                      readOnly={readOnly}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
    </>
  );
}
